/* ══════════════════════════════════════════════════════════════════════════
 * GETTING THERE — ELEAT HANDS YOU OVER TO GOOGLE MAPS
 *
 * Al, 25 September 2026: *"We have to use fucking Google Maps because the
 * restaurants are on Google Maps as well… it should tell you how far you are
 * from that fucking restaurant, whether you're going by walking, by car…
 * as soon as a person finds out where the location of the restaurant, they hit
 * that Google map. We have to know where they are standing so they can
 * actually get to that fucking thing. That's the direction."*
 *
 * ⚠️ NOTHING IN ELEAT DID THIS. The venue page knew the address and the
 * coordinates, the app knew where the Eleater was standing, and there was no
 * control anywhere that put the two together. Every other discovery app has
 * had this since the day it shipped.
 *
 * ── WHY A LINK AND NOT A MAP ────────────────────────────────────────────────
 *
 * This is what Yelp, OpenTable and DoorDash actually do, and it is the right
 * answer rather than the cheap one:
 *
 *   · It opens the Google Maps APP on a phone, not a webpage. Live turn-by-turn,
 *     re-routing, traffic, transit times — the whole thing, already installed,
 *     already signed in, already the thing people trust to walk them somewhere.
 *   · It costs Eleat nothing and needs no key. The Maps URL scheme is public.
 *     Drawing our own map would mean the Maps JavaScript API, a key on every
 *     phone, and a bill per map load — for a worse map.
 *   · ⚠️ AND ELEAT NEVER PRETENDS TO NAVIGATE. A half-built map that loses you
 *     on a walk is worse than no map, and it is the same class of promise as
 *     "you're all set" on a table Eleat never booked.
 *
 * ⚠️ THE ORIGIN IS WHERE THEY ARE *NOW*, NOT WHERE THEY OPENED THE APP.
 * `place.js` follows them; this reads that. If Eleat does not know where they
 * are, the origin is simply left out and Google asks — never a guessed one.
 *
 * ⚠️ AND THE DESTINATION IS THE COORDINATE, NEVER THE NAME. Two restaurants
 * share a name in every city on earth; `location_lat`/`location_lng` are on
 * `restaurant_accounts` (migration 001) and they are exact. The name rides
 * along only as the label Google shows on the pin.
 * ══════════════════════════════════════════════════════════════════════════ */

/* The Google Maps URL scheme — https://developers.google.com/maps/documentation
 * /urls/get-started. `api=1` is the versioned, documented form; the older
 * `maps.google.com/?saddr=` shapes are not guaranteed. */
const MAPS = 'https://www.google.com/maps/dir/?api=1';

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️ I SAID "GOOGLE'S OWN FOUR" AND IT IS FIVE. Al, 25 September 2026: *"on
 * google maps you can walk, bike, train/bus, drive, boat and airplane. so what
 * the hell are you talking about."*
 *
 * Read off https://developers.google.com/maps/documentation/urls/get-started
 * on 25 September 2026 rather than remembered. `travelmode` takes exactly:
 *   driving · walking · bicycling · **two-wheeler** · transit
 * I had missed `two-wheeler` — motorbikes and scooters, which in Hong Kong,
 * Bangkok, Jakarta, Manila and half of Eleat's future markets is how a great
 * many people actually travel. That omission was mine, not Google's.
 *
 * ⚠️ AND HE IS RIGHT ABOUT BOATS AND PLANES TOO — they are simply not
 * `travelmode` values. Ferries and flights live INSIDE Google Maps, which is
 * the point: this parameter only decides which tab Maps OPENS on. Every mode
 * Google Maps has is one tap away the instant it opens, including the ferry
 * across the harbour. Eleat is choosing the first screen, never the options.
 * ══════════════════════════════════════════════════════════════════════════ */
export const MODES = ['walking', 'driving', 'bicycling', 'two-wheeler', 'transit'];

/* ⚠️ THE MODE ELEAT OPENS ON IS A GUESS AND IS MEANT TO BE CHANGED. Under a
 * kilometre most people walk; past that they do not. Google's own screen lets
 * them switch in one tap, so this only has to be sensible, never right. */
const WALKABLE_KM = 1;

export function defaultMode(distanceKm) {
  const km = Number(distanceKm);
  if (!Number.isFinite(km)) return 'driving';
  return km <= WALKABLE_KM ? 'walking' : 'driving';
}

export function canRoute(venue) {
  return !!(venue && Number.isFinite(Number(venue.location_lat))
    && Number.isFinite(Number(venue.location_lng)));
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️⚠️ ELEAT USED TO SEND AN ORIGIN, AND IT WAS THE WRONG PLACE.
 *
 * Al, 25 September 2026: *"Do NOT use `eleat.centre` as the Google Maps
 * navigation origin. That is wrong because `eleat.centre` may be Calgary while
 * I am physically in Vancouver… Prefer allowing Google Maps itself to use the
 * device's current location as the origin."*
 *
 * `Venue.jsx` handed this the browsing centre. Browse Calgary from Vancouver,
 * tap Directions, and Eleat told Google the walk STARTS in Calgary — a route
 * between two points the person is not standing at either end of. It looked
 * right in Hong Kong, where the two are the same place, which is why it lived.
 *
 * ⚠️ THE ANSWER IS TO SEND NOTHING. With no `origin`, Google Maps starts from
 * the device's own current position — the real one, permissioned by Google,
 * refreshed by Google, and correct even when Eleat's fix is a minute stale.
 * Eleat's job ends at naming the destination.
 *
 * @param venue  a row carrying location_lat / location_lng
 * @param mode   one of MODES; falls back to the distance-based guess
 * ══════════════════════════════════════════════════════════════════════════ */
/* ⚠️⚠️ `here` IS THE PHONE'S OWN FIX, NEVER THE BROWSING CENTRE. Al, 6 Oct
   2026, in Shenzhen: Google's "Your location" put him in Hong Kong, 16 km from
   a restaurant Eleat itself measured as 33 km away. With no origin Google
   GUESSES (a laptop has no GPS; a VPN moves the guess again). Eleat already
   holds the fix it measured the distance from, so it sends that. No fix →
   no origin, and Google uses the device as before. */
export function directionsHref(venue, mode, here = null) {
  if (!canRoute(venue)) return null;

  const dest = `${Number(venue.location_lat)},${Number(venue.location_lng)}`;
  const travel = MODES.includes(mode) ? mode : defaultMode(venue.distance_km);

  /* ⚠️ `dir_action=navigate` STARTS TURN-BY-TURN, NOT A ROUTE PREVIEW — and I
     had left it off. Al: *"as you're walking along, fucking Google Maps."*
     Google's own documentation: it *"Launches either turn-by-turn navigation
     or route preview to the specified destination, based on whether the origin
     is available… If the origin is close to the user's current location, the
     map launches turn-by-turn navigation."*  Eleat's origin IS where they are
     standing, so this opens them straight into walking directions rather than
     a screen they have to tap Start on. Where navigation is not offered,
     Google ignores the parameter — it can never break the link. */
  /* ⚠️ NO `origin` PARAMETER, DELIBERATELY — see the block above. Google's own
     documentation: with no origin it uses the device's current location, and
     `dir_action=navigate` then starts turn-by-turn rather than a preview. */
  const from = (here && Number.isFinite(Number(here.lat)) && Number.isFinite(Number(here.lng)))
    ? `&origin=${encodeURIComponent(`${Number(here.lat)},${Number(here.lng)}`)}` : '';
  return `${MAPS}${from}&destination=${encodeURIComponent(dest)}`
    + `&travelmode=${travel}&dir_action=navigate`;
}

/* ══════════════════════════════════════════════════════════════════════════
 * HOW FAR IT IS — AND WHAT THAT NUMBER HONESTLY MEANS
 *
 * ⚠️ `distance_km` FROM THE SEARCH IS A STRAIGHT LINE, NOT A WALK. It is the
 * crow-flies distance the database computes, and in Hong Kong — harbour,
 * hills, one-way streets, a building you cannot walk through — the real walk
 * is reliably longer. Printing it as "8 minutes" would be a number Eleat made
 * up, which is the fault this repository keeps paying for.
 *
 * So it is labelled as what it is, and the true travel time is Google's to
 * give the moment they tap through. A real door-to-door time inside Eleat
 * needs the Routes API, which is metered per request — Al's call, and not one
 * to take quietly.
 * ══════════════════════════════════════════════════════════════════════════ */
/**
 * @param {number|string|null|undefined} distanceKm
 * @param {number|null} accuracyM the phone's own stated error, when the
 *   distance was measured FROM the phone. Omit for a chosen place, which has
 *   no error — somebody who picked Central is exactly in Central.
 *
 * ⚠️⚠️ THE SECOND ARGUMENT EXISTS BECAUSE OF iOS 14 AND ANDROID 12 — 28 Sep
 * 2026. Both let somebody grant a deliberately fuzzy position, and Apple
 * documents that fuzz at 1–20 km. **"400 m away" measured from a point five
 * kilometres out is a fabricated number**, and it renders exactly as well as a
 * real one — nothing throws, nothing logs, and the Eleater walks the wrong
 * way. This is the same fault as `howFar(null)` printing "0 m away": a
 * confident wrong sentence, which this repository keeps paying for.
 *
 * ⚠️ IT SAYS NOTHING RATHER THAN SAYING IT VAGUELY, AND THAT IS RULE 4. There
 * is no approved Eleat copy for an approximate distance — no "about 2 km", no
 * "roughly" — so inventing one would be inventing Eleat. Every caller already
 * handles a null (that is what an unknown distance has always returned), so
 * the row simply does not draw. **Directions is unaffected**: it hands Google
 * the coordinates and Google routes from the phone, at whatever accuracy the
 * phone is willing to give it.
 */
export function howFar(distanceKm, accuracyM = null) {
  /* ⚠️ `Number(null)` IS 0, AND 0 IS A LEGITIMATE DISTANCE. So "we do not know
     how far" and "you are standing at the door" arrived here identical, and
     every venue Eleat had no position for read "0 m away". Guarding on the
     INPUT, before Number() ever sees it, is the only way to tell them apart —
     checking the number afterwards cannot, which is why the first fix missed
     it and a real call found it. */
  if (distanceKm === null || distanceKm === undefined || distanceKm === '') return null;
  const km = Number(distanceKm);
  if (!Number.isFinite(km) || km < 0) return null;

  /* ⚠️ NEVER STATE A DISTANCE FINER THAN THE ERROR IT WAS MEASURED WITH.
     ⚠️ AND 110 IS NOT A NUMBER I PICKED — it is `roundKey`'s own grid in
     where.service.js, already in this codebase with its own written reason:
     fine enough to name a neighbourhood. An ordinary GPS fix in Hong Kong's
     urban canyons measures 6.7–13 m and sails past it; a reduced-accuracy fix
     is kilometres and cannot. */
  const err = Number(accuracyM);
  if (Number.isFinite(err) && err > 110) {
    /* the error swallows the whole distance — there is nothing honest to say */
    if (err >= km * 1000) return null;
    /* under a kilometre, with an error over 110 m, the metres are noise */
    if (km < 1) return null;
    return `${Math.round(km)} km away`;
  }

  if (km < 1) return `${Math.round(km * 1000)} m away`;
  return `${km.toFixed(1)} km away`;
}
